import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { environment } from "environment/environment";
import {
  saveShopifyOnboardingSession,
  shopifyOnboardingPath,
} from "services/ShopifyOnboardingSession";
import {
  saveWooOnboardingSession,
  wooOnboardingPath,
} from "services/WooCommerceOnboardingSession";
import {
  merchantPortalLogout,
  saveCustomOnboardingSession,
} from "services/MerchantPortalSession";
import { getMerchantAppBranding, isLiveMerchantPlatform, parseMerchantPlatform } from "./merchantBranding";
import {
  appendDashboardRangeParams,
  createDefaultDateFilter,
  DashboardDateFilter,
  DashboardDatePreset,
  formatDashboardDateFilterLabel,
  IconRefresh,
  isDefaultDateFilter,
  isoDate,
  MerchantOrder,
  MerchantOrdersPanel,
  OrderSheet,
  OrdersTable,
  OrderListFilter,
  startOfLocalDay,
} from "./MerchantOrders";
import MerchantSettings, { LabelFormatId, LabelFormatOption } from "./MerchantSettings";
import {
  DEFAULT_LABEL_FORMAT,
  mergeLabelFormats,
} from "./labelFormats";
import MerchantNotifications from "./MerchantNotifications";
import MerchantEmptyState from "./MerchantEmptyState";
import MerchantLoader from "./MerchantLoader";
import MerchantHelpCenter from "./MerchantHelpCenter";
import MerchantGetStarted from "./MerchantGetStarted";
import {
  GetStartedPanel,
  hasCompletedGetStarted,
} from "./merchantGetStartedState";
import { useMerchantLiveSync } from "./useMerchantLiveSync";
import "./ShopifyMerchantDashboard.css";

interface DashboardPayload {
  platform?: string;
  shop?: string;
  storeName?: string;
  connectionStatus?: string;
  pricingStatus?: string;
  merchant?: { name?: string | null; email?: string | null };
  statusBreakdown?: {
    inTransit?: number;
    delivered?: number;
    total?: number;
  };
  orders?: MerchantOrder[];
  labelFormat?: LabelFormatId;
  labelFormats?: LabelFormatOption[];
  unreadNotifications?: number;
}

const isSessionErrorCode = (code?: string): boolean =>
  code === "WOO_SESSION_INVALID" ||
  code === "WOO_SESSION_REQUIRED" ||
  code === "SHOPIFY_SESSION_INVALID" ||
  code === "SHOPIFY_SESSION_REQUIRED" ||
  code === "CUSTOM_SESSION_INVALID" ||
  code === "CUSTOM_SESSION_REQUIRED";

const connectionCopy = (
  status?: string,
  load?: "ready" | "loading" | "failed"
): { label: string; hint: string; tone: "ok" | "warn" | "muted" } => {
  const value = String(status || "").toLowerCase();
  if (value === "disconnected") {
    return {
      label: "Disconnected",
      hint: "This shop is not linked. Reconnect it to send orders to Zulu.",
      tone: "warn",
    };
  }
  if (value === "requires_attention") {
    return {
      label: "Needs attention",
      hint: "The store link needs a check. Open the plugin and connect again.",
      tone: "warn",
    };
  }
  if (value === "connected") {
    return {
      label: "Connected",
      hint: "This shop is linked. New orders can come through to Zulu.",
      tone: "ok",
    };
  }
  if (load === "failed") {
    return {
      label: "Unavailable",
      hint: "Open Zulu Courier in the shop admin and click Open Zulu onboarding.",
      tone: "warn",
    };
  }
  return { label: "Checking…", hint: "Loading the store connection.", tone: "muted" };
};

const pricingCopy = (
  status?: string,
  load?: "ready" | "loading" | "failed"
): { label: string; hint: string; tone: "ok" | "pending" | "muted" } => {
  const value = String(status || "").toLowerCase();
  if (value === "active") {
    return {
      label: "Active",
      hint: "Zulu delivery rates are live at checkout.",
      tone: "ok",
    };
  }
  if (value === "pending") {
    return {
      label: "Pending approval",
      hint: "Zulu is reviewing this store. Rates will show after approval.",
      tone: "pending",
    };
  }
  if (load === "failed") {
    return {
      label: "Unavailable",
      hint: "Open a fresh dashboard link from the shop plugin.",
      tone: "muted",
    };
  }
  return { label: "Checking…", hint: "Loading checkout pricing status.", tone: "muted" };
};

type Panel = "dashboard" | "orders" | "notifications" | "settings" | "help";

const initials = (name?: string | null): string => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) {
    return "ZU";
  }
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "ZU";
};

interface MerchantDashboardProps {
  platform?: string;
}

const MerchantDashboard: React.FC<MerchantDashboardProps> = ({ platform: platformProp }) => {
  const { platform: platformParam } = useParams();
  const [searchParams] = useSearchParams();
  const platform = parseMerchantPlatform(platformProp || platformParam);
  const branding = getMerchantAppBranding(platform);
  const shop = searchParams.get("shop") || "";
  const session = searchParams.get("session") || "";
  const [panel, setPanel] = useState<Panel>("dashboard");
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [loadingStats, setLoadingStats] = useState(Boolean(session));
  const [dashboardError, setDashboardError] = useState("");
  const [dashboardDateFilter, setDashboardDateFilter] = useState<DashboardDateFilter>(
    createDefaultDateFilter
  );
  const [ordersDateFilter, setOrdersDateFilter] = useState<DashboardDateFilter>(
    createDefaultDateFilter
  );
  const [ordersStatus, setOrdersStatus] = useState<OrderListFilter>("all");
  const [viewing, setViewing] = useState<MerchantOrder | null>(null);
  const [labelFormat, setLabelFormat] = useState<LabelFormatId>(DEFAULT_LABEL_FORMAT);
  const [labelFormats, setLabelFormats] = useState<LabelFormatOption[]>(
    mergeLabelFormats()
  );
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [getStartedOpen, setGetStartedOpen] = useState(false);
  const getStartedChecked = useRef(false);
  const dataRef = useRef<DashboardPayload | null>(null);
  const dashboardRequest = useRef(0);
  const liveEnabled = Boolean(session) && isLiveMerchantPlatform(platform);
  const { tick, bump } = useMerchantLiveSync(liveEnabled, Boolean(viewing));
  const labelContext = useMemo(
    () => ({ platform, shop, session, format: labelFormat }),
    [platform, shop, session, labelFormat]
  );

  const openPanel = (next: Panel) => {
    if (next === panel) {
      if (!viewing) {
        bump();
      }
      return;
    }
    setPanel(next);
  };

  useEffect(() => {
    if (!liveEnabled || getStartedChecked.current) {
      return;
    }
    getStartedChecked.current = true;
    if (!hasCompletedGetStarted(platform, shop)) {
      setGetStartedOpen(true);
    }
  }, [liveEnabled, platform, shop]);

  const closeOrder = useCallback(() => {
    setViewing(null);
    bump();
  }, [bump]);

  const openOrders = (status: OrderListFilter = "all") => {
    setOrdersDateFilter(dashboardDateFilter);
    setOrdersStatus(status);
    openPanel("orders");
  };

  const loadDashboard = useCallback(async (options?: { silent?: boolean }) => {
    if (!session || !isLiveMerchantPlatform(platform)) {
      return;
    }
    if (platform === "shopify") {
      saveShopifyOnboardingSession(session);
    } else if (platform === "woocommerce") {
      saveWooOnboardingSession(session);
    } else if (platform === "custom") {
      saveCustomOnboardingSession(session);
    }
    const silent = Boolean(options?.silent) && Boolean(dataRef.current);
    const requestId = dashboardRequest.current + 1;
    dashboardRequest.current = requestId;
    const params = new URLSearchParams({ session });
    appendDashboardRangeParams(params, dashboardDateFilter);
    if (shop) {
      params.set("shop", shop);
    }
    if (!silent) {
      setLoadingStats(true);
      setDashboardError("");
    }
    try {
      const response = await fetch(
        `${environment.api_url}/api/v1/integrations/${platform}/dashboard?${params.toString()}`
      );
      const body = await response.json();
      if (requestId !== dashboardRequest.current) {
        return;
      }
      if (response.ok && body.success !== false) {
        if (body.data && body.data.registrationRequired) {
          const onboardingPath =
            platform === "woocommerce"
              ? wooOnboardingPath(session, shop)
              : shopifyOnboardingPath(session, shop);
          window.location.replace(onboardingPath);
          return;
        }
        const payload = (body.data || {}) as DashboardPayload;
        dataRef.current = payload;
        setData(payload);
        const nextFormat = payload.labelFormat || DEFAULT_LABEL_FORMAT;
        setLabelFormat(nextFormat);
        setLabelFormats(mergeLabelFormats(payload.labelFormats, nextFormat));
        if (typeof payload.unreadNotifications === "number") {
          setUnreadNotifications(payload.unreadNotifications);
        }
        setDashboardError("");
        return;
      }
      if (silent) {
        return;
      }
      if (isSessionErrorCode(String(body.errorCode || ""))) {
        setDashboardError(
          "This dashboard link expired. In WooCommerce, open Zulu Courier and click Open Zulu onboarding."
        );
        return;
      }
      setDashboardError(body.error || body.message || "Could not load dashboard.");
    } catch {
      if (requestId !== dashboardRequest.current || silent) {
        return;
      }
      setDashboardError(
        "Could not reach the Zulu API. Make sure it is running on port 3015."
      );
    } finally {
      if (requestId === dashboardRequest.current) {
        setLoadingStats(false);
      }
    }
  }, [session, shop, platform, dashboardDateFilter]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const loadDashboardRef = useRef(loadDashboard);
  loadDashboardRef.current = loadDashboard;

  useEffect(() => {
    if (tick === 0) {
      return;
    }
    loadDashboardRef.current({ silent: true });
  }, [tick]);

  useEffect(() => {
    document.title = `${PAGE_COPY[panel].title} · Zulu Courier`;
  }, [panel]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add("zulu-plugin-lock");
    body.classList.add("zulu-plugin-lock");
    return () => {
      root.classList.remove("zulu-plugin-lock");
      body.classList.remove("zulu-plugin-lock");
    };
  }, []);

  const shopName = data?.storeName || data?.shop || shop || `${branding.label} store`;
  const merchantName = data?.merchant?.name || "Merchant";
  const PlatformLogo = branding.Logo;
  const page = PAGE_COPY[panel];
  const statusLoad =
    data ? "ready" : dashboardError ? "failed" : "loading";
  const storeStatus = connectionCopy(data?.connectionStatus, statusLoad);
  const pricingStatus = pricingCopy(data?.pricingStatus, statusLoad);
  const recentOrders = [...(data?.orders || [])]
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
    .slice(0, 8);

  return (
    <div className="zulu-plugin" data-platform={platform}>
      <aside className="zulu-plugin__sidebar">
        <div className="zulu-plugin__brand">
          <ZuluLogo />
          <small>{branding.pluginLabel}</small>
        </div>
        <nav className="zulu-plugin__nav">
          <button className={panel === "dashboard" ? "active" : ""} type="button" onClick={() => openPanel("dashboard")}>
            <IconGrid /> Dashboard
          </button>
          <button className={panel === "orders" ? "active" : ""} type="button" onClick={() => openPanel("orders")}>
            <IconList /> Orders
          </button>
          <button className={panel === "notifications" ? "active" : ""} type="button" onClick={() => openPanel("notifications")}>
            <IconBell /> Notifications
            {unreadNotifications > 0 ? (
              <span className="zulu-plugin__nav-badge" aria-label={`${unreadNotifications} unread notifications`}>
                {unreadNotifications > 99 ? "99+" : unreadNotifications}
              </span>
            ) : null}
          </button>
          <button className={panel === "settings" ? "active" : ""} type="button" onClick={() => openPanel("settings")}>
            <IconGear /> Settings
          </button>
          <button className={panel === "help" ? "active" : ""} type="button" onClick={() => openPanel("help")}>
            <IconHelp /> Help
          </button>
        </nav>
        <div className="zulu-plugin__sidebar-foot">
          <div className="zulu-plugin__who" title={shopName}>
            <PlatformLogo />
            <strong>{shopName}</strong>
          </div>
          <div className="zulu-plugin__who" title={merchantName}>
            <span className="zulu-plugin__avatar">{initials(merchantName)}</span>
            <strong>{merchantName}</strong>
          </div>
          <button
            type="button"
            className="zulu-plugin__logout"
            onClick={() => merchantPortalLogout()}
          >
            <IconLogout /> Log out
          </button>
        </div>
      </aside>
      <main className="zulu-plugin__main">
        <header className={`zulu-plugin__stage-head${panel === "dashboard" ? " is-dashboard" : ""}`}>
          <div className="zulu-plugin__stage-head-bg" aria-hidden="true" />
          <div className="zulu-plugin__stage-copy">
            <p className="zulu-plugin__kicker">
              <span className="zulu-plugin__pulse" aria-hidden="true" />
              {branding.label}
            </p>
            <h1>{page.title}</h1>
            <p className="zulu-plugin__stage-shop">{shopName}</p>
            {panel === "dashboard" ? (
              <div className="zulu-plugin__store-status" aria-label="Store connection and pricing status">
                <article className={`zulu-plugin__store-status-item is-${storeStatus.tone}`}>
                  <span className="zulu-plugin__store-status-label">Store connection</span>
                  <strong>{storeStatus.label}</strong>
                  <p>{storeStatus.hint}</p>
                </article>
                <article className={`zulu-plugin__store-status-item is-${pricingStatus.tone}`}>
                  <span className="zulu-plugin__store-status-label">Checkout pricing</span>
                  <strong>{pricingStatus.label}</strong>
                  <p>{pricingStatus.hint}</p>
                </article>
              </div>
            ) : null}
          </div>
          {panel === "dashboard" ? (
            <DateCalendarFilter
              value={dashboardDateFilter}
              onChange={setDashboardDateFilter}
            />
          ) : null}
          {panel === "orders" ? (
            <DateCalendarFilter
              value={ordersDateFilter}
              onChange={setOrdersDateFilter}
            />
          ) : null}
        </header>
        <div className="zulu-plugin__stage">
        {panel === "dashboard" ? (
          <section className="zulu-plugin__stage-body is-dashboard">
            {loadingStats && !data ? (
              <MerchantLoader size="page" label="Loading your deliveries…" />
            ) : dashboardError && !data ? (
              <div className="zulu-plugin__empty-card">
                <MerchantEmptyState
                  kind="error"
                  layout="page"
                  title="Could not open this store"
                  description={dashboardError}
                  actionLabel="Try again"
                  onAction={() => loadDashboard()}
                />
              </div>
            ) : (
              <>
            {dashboardError ? <p className="zulu-plugin__orders-error">{dashboardError}</p> : null}
            <div className="zulu-plugin__stats-head">
              <div>
                <h2>Orders</h2>
                <p>{formatDashboardDateFilterLabel(dashboardDateFilter)}</p>
              </div>
              <button
                type="button"
                className="zulu-plugin__refresh-btn"
                onClick={() => loadDashboard()}
                disabled={loadingStats}
                aria-busy={loadingStats}
                aria-label="Refresh dashboard"
              >
                <IconRefresh />
                <span>{loadingStats ? "Refreshing…" : "Refresh"}</span>
              </button>
            </div>
            {loadingStats ? (
              <MerchantLoader size="inline" label="Updating deliveries…" />
            ) : null}
            <div className={`zulu-plugin__stats${loadingStats ? " is-loading" : ""}`}>
              <article className="zulu-plugin__stat">
                <div className="zulu-plugin__stat-icon"><IconTruck /></div>
                <div>
                  <p>In transit</p>
                  <strong>{data?.statusBreakdown?.inTransit ?? 0}</strong>
                  <button type="button" onClick={() => openOrders("inTransit")}>View orders</button>
                </div>
              </article>
              <article className="zulu-plugin__stat">
                <div className="zulu-plugin__stat-icon"><IconCheck /></div>
                <div>
                  <p>Delivered</p>
                  <strong>{data?.statusBreakdown?.delivered ?? 0}</strong>
                  <button type="button" onClick={() => openOrders("delivered")}>View orders</button>
                </div>
              </article>
              <article className="zulu-plugin__stat">
                <div className="zulu-plugin__stat-icon"><IconFile /></div>
                <div>
                  <p>Total orders</p>
                  <strong>{data?.statusBreakdown?.total ?? 0}</strong>
                  <button type="button" onClick={() => openOrders("all")}>View all orders</button>
                </div>
              </article>
            </div>
            <div className="zulu-plugin__orders">
              <div className="zulu-plugin__orders-head">
                <div>
                  <h2>Recent orders</h2>
                  <p>Latest deliveries from this store</p>
                </div>
                <button type="button" onClick={() => openOrders("all")}>
                  View all
                </button>
              </div>
              <OrdersTable
                orders={recentOrders}
                loading={loadingStats}
                emptyKind={isDefaultDateFilter(dashboardDateFilter) ? "recent" : "filters"}
                emptyActionLabel={
                  isDefaultDateFilter(dashboardDateFilter) ? "View all orders" : "Show today"
                }
                onEmptyAction={
                  isDefaultDateFilter(dashboardDateFilter)
                    ? () => openOrders("all")
                    : () => setDashboardDateFilter(createDefaultDateFilter())
                }
                onView={setViewing}
                labelContext={labelContext}
              />
            </div>
              </>
            )}
          </section>
        ) : null}
        {liveEnabled ? (
          <div className="zulu-plugin__stage-panel" hidden={panel !== "orders"}>
            <MerchantOrdersPanel
              platform={platform}
              shop={shop}
              session={session}
              dateFilter={ordersDateFilter}
              statusFilter={ordersStatus}
              onStatusFilter={setOrdersStatus}
              onResetDateFilter={() => setOrdersDateFilter(createDefaultDateFilter())}
              labelFormat={labelFormat}
              refreshKey={tick}
              onViewOrder={setViewing}
            />
          </div>
        ) : null}
        {panel === "settings" ? (
          <div className="zulu-plugin__stage-panel">
            <MerchantSettings
              platform={platform}
              shop={shop}
              session={session}
              storeName={shopName}
              initialFormat={labelFormat}
              initialFormats={labelFormats}
              onFormatChange={setLabelFormat}
            />
          </div>
        ) : null}
        {liveEnabled ? (
          <div className="zulu-plugin__stage-panel" hidden={panel !== "notifications"}>
            <MerchantNotifications
              platform={platform}
              shop={shop}
              session={session}
              refreshKey={tick}
              onUnreadChange={setUnreadNotifications}
              onViewOrders={() => openPanel("orders")}
              onViewOrder={setViewing}
            />
          </div>
        ) : null}
        {panel === "help" ? (
          <MerchantHelpCenter
            platform={platform}
            storeName={shopName}
            onReplayGetStarted={() => setGetStartedOpen(true)}
          />
        ) : null}
        </div>
        <OrderSheet
          order={viewing}
          onClose={closeOrder}
          labelContext={labelContext}
        />
      </main>
      <MerchantGetStarted
        platform={platform}
        shop={shop}
        storeName={shopName}
        open={getStartedOpen}
        onClose={() => setGetStartedOpen(false)}
        onNavigate={(next: GetStartedPanel) => {
          setGetStartedOpen(false);
          openPanel(next);
        }}
      />
    </div>
  );
};

const PAGE_COPY: Record<Panel, { title: string }> = {
  dashboard: { title: "Dashboard" },
  orders: { title: "Orders" },
  notifications: { title: "Notifications" },
  settings: { title: "Settings" },
  help: { title: "Help" },
};

const RANGE_PRESET_CHIPS: Array<{ id: Exclude<DashboardDatePreset, "today" | "custom">; label: string }> = [
  { id: "week", label: "Weekly" },
  { id: "month", label: "Monthly" },
];

const DateCalendarFilter: React.FC<{
  value: DashboardDateFilter;
  onChange: (next: DashboardDateFilter) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const base = value.day || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const rootRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const todayIso = isoDate(new Date());
  const pickedIso = value.preset === "custom" ? isoDate(value.day) : "";
  const start = new Date(view.getFullYear(), view.getMonth(), 1);
  const cursor = new Date(start);
  cursor.setDate(1 - start.getDay());
  const days = Array.from({ length: 42 }, () => {
    const day = new Date(cursor);
    cursor.setDate(cursor.getDate() + 1);
    return day;
  });

  const openPicker = () => {
    const day =
      value.preset === "custom" ? value.day : startOfLocalDay(new Date());
    if (value.preset !== "custom") {
      onChange({ preset: "custom", day });
    }
    setView(new Date(day.getFullYear(), day.getMonth(), 1));
    setOpen((current) => (value.preset === "custom" ? !current : true));
  };

  return (
    <div className={`zulu-plugin__filter${open ? " is-open" : ""}`} ref={rootRef}>
      <div className="zulu-plugin__filter-bar" role="group" aria-label="Date range">
        <div className="zulu-plugin__filter-picker">
          <button
            type="button"
            className={`zulu-plugin__filter-chip${value.preset === "custom" ? " on" : ""}`}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-pressed={value.preset === "custom"}
            onClick={openPicker}
          >
            <IconCal />
            <span>
              {value.preset === "custom"
                ? value.day.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "Pick a date"}
            </span>
          </button>
          {open ? (
            <div className="zulu-plugin__cal" role="dialog" aria-label="Pick a single day">
              <div className="zulu-plugin__cal-nav">
                <button
                  type="button"
                  aria-label="Previous month"
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                >
                  ‹
                </button>
                <strong>
                  {view.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                </strong>
                <button
                  type="button"
                  aria-label="Next month"
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                >
                  ›
                </button>
              </div>
              <div className="zulu-plugin__cal-week">
                {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
                  <span key={`${day}-${index}`}>{day}</span>
                ))}
              </div>
              <div className="zulu-plugin__cal-grid">
                {days.map((day) => {
                  const key = isoDate(day);
                  const inFuture = key > todayIso;
                  const classes = [
                    day.getMonth() !== view.getMonth() ? "mute" : "",
                    key === todayIso ? "today" : "",
                    key === pickedIso ? "picked" : "",
                    inFuture ? "mute" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <button
                      key={key + day.getMonth()}
                      type="button"
                      className={classes}
                      disabled={inFuture}
                      onClick={() => {
                        onChange({
                          preset: "custom",
                          day: startOfLocalDay(day),
                        });
                        setOpen(false);
                      }}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
        {RANGE_PRESET_CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={`zulu-plugin__filter-chip${value.preset === chip.id ? " on" : ""}`}
            aria-pressed={value.preset === chip.id}
            onClick={() => {
              onChange({
                preset: chip.id,
                day: value.day || startOfLocalDay(new Date()),
              });
              setOpen(false);
            }}
          >
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const ZuluLogo = () => (
  <svg width="91" height="36" viewBox="0 0 91 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Zulu">
    <path d="M6.90131 30.145H25.1614V35.4156H0V30.145L18.2107 6.52512H0.831956V1.25452H25.112V6.52512L6.90131 30.145Z" fill="#F75D21" />
    <path d="M47.2859 8.5791H52.817V35.4155H47.2859V30.9743C45.1973 34.3256 42.2114 36.0013 38.3281 36.0013C35.3582 36.0013 33.0248 35.0738 31.328 33.2189C29.6311 31.3639 28.7827 28.7937 28.7827 25.508V8.5791H34.3139V24.3896C34.3139 28.623 36.2391 30.7397 40.0895 30.7397C42.3747 30.7397 44.1452 30.0157 45.401 28.5677C46.6567 27.1197 47.285 25.0942 47.2859 22.491V8.5791Z" fill="#F75D21" />
    <path d="M61.5307 12.0782H61.0107L62.8475 35.4051H57.2669L59.1063 12.0782H58.5955C58.3803 12.0798 58.1732 11.9964 58.0196 11.8462L57.0603 10.9118C56.9824 10.8358 56.9206 10.7451 56.8784 10.6449C56.8362 10.5448 56.8145 10.4373 56.8145 10.3286C56.8145 10.22 56.8362 10.1125 56.8784 10.0124C56.9206 9.91221 56.9824 9.82148 57.0603 9.74546L58.0196 8.81238C58.173 8.66172 58.3801 8.57784 58.5955 8.57911H59.3858L60.0605 0.00256348L60.7338 8.57911H61.5307C61.7456 8.57902 61.9521 8.6622 62.1066 8.81109L63.0646 9.74417C63.1424 9.82018 63.2043 9.91092 63.2465 10.0111C63.2887 10.1112 63.3104 10.2187 63.3104 10.3273C63.3104 10.436 63.2887 10.5435 63.2465 10.6436C63.2043 10.7438 63.1424 10.8345 63.0646 10.9105L62.1066 11.8488C61.9517 11.9967 61.7452 12.0789 61.5307 12.0782Z" fill="#F75D21" />
    <path d="M85.4675 8.5791H90.9987V35.4155H85.4675V30.9743C83.3781 34.3256 80.3922 36.0013 76.5097 36.0013C73.5389 36.0013 71.2056 35.0738 69.5096 33.2189C67.8136 31.3639 66.9652 28.7937 66.9644 25.508V8.5791H72.4955V24.3896C72.4955 28.623 74.4207 30.7397 78.2711 30.7397C80.5564 30.7397 82.3269 30.0157 83.5826 28.5677C84.8383 27.1197 85.4666 25.0942 85.4675 22.491V8.5791Z" fill="#F75D21" />
  </svg>
);

const IconGrid = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
const IconList = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M7 7h13M7 12h13M7 17h13" />
    <circle cx="4" cy="7" r="1" />
    <circle cx="4" cy="12" r="1" />
    <circle cx="4" cy="17" r="1" />
  </svg>
);
const IconBell = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M6 17h12l-1-7a5 5 0 10-10 0l-1 7z" />
    <path d="M10 17a2 2 0 004 0" />
  </svg>
);
const IconGear = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2M12 19v2M4.2 6.2l1.4 1.4M18.4 16.4l1.4 1.4M3 12h2M19 12h2M4.2 17.8l1.4-1.4M18.4 7.6l1.4-1.4" />
  </svg>
);
const IconLogout = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const IconHelp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9a2.5 2.5 0 114 2c0 1.5-2 1.5-2 3" />
    <circle cx="12" cy="17" r=".5" />
  </svg>
);
const IconCal = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);
const IconTruck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M3 7h11v10H3z" />
    <path d="M14 10h4l3 3v4h-7" />
    <circle cx="7" cy="18" r="1.6" />
    <circle cx="18" cy="18" r="1.6" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="8" />
    <path d="M8.5 12.2l2.3 2.3 4.7-5" />
  </svg>
);
const IconFile = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M7 4h7l4 4v12H7z" />
    <path d="M14 4v4h4" />
  </svg>
);

export default MerchantDashboard;
